import { z } from 'zod';

/**
 * Event payload parsing (V1-05). Payloads are INVALIDATION HINTS ONLY
 * (06-realtime.md §3): parse the minimal identifier and reconcile over HTTP.
 * Payload money/customer/stock fields are NEVER authoritative and are NEVER
 * logged (09-quality.md §6).
 */

const minimalOrderIdSchema = z.looseObject({
  order: z.looseObject({ id: z.number() }),
});

const newOrderHintSchema = z.looseObject({
  order: z.looseObject({
    id: z.number(),
    order_number: z.string().optional(),
    source: z.enum(['web', 'desktop_pos', 'mobile_pos']).optional(),
    pos_terminal_id: z.number().nullable().optional(),
  }),
});

export interface NewOrderHint {
  id: number;
  orderNumber: string | null;
  source: 'web' | 'desktop_pos' | 'mobile_pos' | null;
}

/**
 * The order id when consistently present in the publisher payload
 * (salesgroundai events wrap `{ order: { id, … } }`); null means "identifier
 * missing — refresh the whole active board".
 */
export function extractOrderId(payload: unknown): number | null {
  const parsed = minimalOrderIdSchema.safeParse(payload);
  return parsed.success ? parsed.data.order.id : null;
}

/**
 * Safe, non-customer realtime fields used only to announce promptly. HTTP
 * reconciliation remains authoritative for the actual order card and totals.
 */
export function extractNewOrderHint(payload: unknown): NewOrderHint | null {
  const parsed = newOrderHintSchema.safeParse(payload);
  if (!parsed.success) return null;

  const order = parsed.data.order;
  const source =
    order.source ??
    (order.pos_terminal_id !== undefined
      ? order.pos_terminal_id === null
        ? 'web'
        : 'desktop_pos'
      : null);

  return {
    id: order.id,
    orderNumber: order.order_number?.trim() || null,
    source,
  };
}
